<script>
/**
 * O anel de progresso do PLANO do turno (cartão flutuante do painel: cabeçalho
 * do Progresso e botão recolhido) — só é montado quando há plano explícito (`plano`). SVG
 * próprio, cor `primary`:
 *
 * - `encerrado` (todas as etapas concluídas/falhas): `check-circle` primary;
 * - com fração (`concluidas`/`total` do plano): o arco da fração REAL;
 * - sem fração e `emAndamento`: arco curto GIRANDO (indeterminado) — nunca
 *   uma fração inventada.
 *
 * O estado vai no `aria-label` (`role="img"`). `prefers-reduced-motion`
 * para o giro (o arco fica parado).
 */
import { PAINEL_DO_CHAT } from '@modules/aawz-intelligence-v2/constants/TextChat'
import {
  ARCO_INDETERMINADO,
  ICONE_DE_CONCLUIDO,
  LADO_DO_ANEL,
  RAIO_DO_ANEL,
  TRACO_DO_ANEL,
} from './painel-do-chat.constantes'

const CIRCUNFERENCIA = 2 * Math.PI * RAIO_DO_ANEL

export default {
  name: 'IntelligenceV2PainelAnelProgresso',
  props: {
    emAndamento: {
      type: Boolean,
      default: false,
    },
    encerrado: {
      type: Boolean,
      default: false,
    },
    concluidas: {
      type: Number,
      default: null,
    },
    total: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      ICONE_DE_CONCLUIDO,
      LADO_DO_ANEL,
      RAIO_DO_ANEL,
      TRACO_DO_ANEL,
      centro: LADO_DO_ANEL / 2,
      viewBox: '0 0 ' + LADO_DO_ANEL + ' ' + LADO_DO_ANEL,
    }
  },
  computed: {
    temFracao() {
      return this.total !== null && this.total > 0 && this.concluidas !== null
    },
    // Sem fração, ou com NADA concluído ainda: gira (um arco parado em 0
    // parecia travado — achado do usuário).
    indeterminado() {
      if (!this.emAndamento) return false
      return !this.temFracao || this.concluidas === 0
    },
    // Com fração e trabalhando: o arco preenchido pulsa e um ponto na ponta
    // "respira" — o sinal de que a próxima etapa está em curso.
    trabalhando() {
      return this.emAndamento && !this.indeterminado
    },
    pontaDoArco() {
      const fracao = this.temFracao ? Math.min(1, this.concluidas / this.total) : 0
      const angulo = fracao * 2 * Math.PI - Math.PI / 2
      return {
        x: this.centro + RAIO_DO_ANEL * Math.cos(angulo),
        y: this.centro + RAIO_DO_ANEL * Math.sin(angulo),
      }
    },
    concluido() {
      return this.encerrado || (!this.emAndamento && !this.temFracao)
    },
    // O arco desenhado: a fração real, ou o trecho fixo que gira.
    tracejado() {
      const fracao = this.indeterminado ? ARCO_INDETERMINADO : this.concluidas / this.total
      const arco = CIRCUNFERENCIA * Math.min(1, Math.max(0, fracao))
      return arco + ' ' + CIRCUNFERENCIA
    },
    rotulo() {
      if (this.encerrado) return PAINEL_DO_CHAT.CONCLUIDO
      if (this.temFracao) return PAINEL_DO_CHAT.FRACAO(this.concluidas, this.total)
      return this.emAndamento ? PAINEL_DO_CHAT.EM_ANDAMENTO : PAINEL_DO_CHAT.CONCLUIDO
    },
  },
}
</script>

<template>
  <span
    class="painel-anel-progresso d-inline-flex align-center justify-center flex-shrink-0"
    role="img"
    :aria-label="rotulo"
    :title="rotulo"
    data-test="painel-anel-progresso"
  >
    <icon-svg
      v-if="concluido"
      :name="ICONE_DE_CONCLUIDO"
      :size="LADO_DO_ANEL"
      color="primary"
      data-test="painel-anel-progresso-concluido"
    />
    <svg
      v-else
      class="painel-anel-progresso__svg"
      :class="{ 'painel-anel-progresso__svg--girando': indeterminado }"
      :width="LADO_DO_ANEL"
      :height="LADO_DO_ANEL"
      :viewBox="viewBox"
      aria-hidden="true"
      data-test="painel-anel-progresso-svg"
    >
      <circle
        class="painel-anel-progresso__trilha"
        :cx="centro"
        :cy="centro"
        :r="RAIO_DO_ANEL"
        :stroke-width="TRACO_DO_ANEL"
        fill="none"
      />
      <circle
        class="painel-anel-progresso__arco"
        :class="{ 'painel-anel-progresso__arco--trabalhando': trabalhando }"
        :cx="centro"
        :cy="centro"
        :r="RAIO_DO_ANEL"
        :stroke-width="TRACO_DO_ANEL"
        :stroke-dasharray="tracejado"
        fill="none"
        stroke-linecap="round"
        data-test="painel-anel-progresso-arco"
      />
      <circle
        v-if="trabalhando"
        class="painel-anel-progresso__ponta"
        :cx="pontaDoArco.x"
        :cy="pontaDoArco.y"
        :r="TRACO_DO_ANEL"
        data-test="painel-anel-progresso-ponta"
      />
    </svg>
  </span>
</template>

<style lang="sass" scoped>
.painel-anel-progresso__trilha
  stroke: var(--neutral-200)

// O arco começa no topo (−90°) e cresce no sentido horário.
.painel-anel-progresso__arco
  stroke: var(--primary)
  transform: rotate(-90deg)
  transform-origin: center
  transition: stroke-dasharray var(--motion-duration-default) var(--motion-easing-standard)

.painel-anel-progresso__svg--girando
  animation: painel-anel-progresso-giro 900ms linear infinite

.painel-anel-progresso__arco--trabalhando
  animation: painel-anel-progresso-pulso 1.2s ease-in-out infinite

.painel-anel-progresso__ponta
  fill: var(--primary)
  transform-box: fill-box
  transform-origin: center
  animation: painel-anel-progresso-respira 1.2s ease-in-out infinite

@keyframes painel-anel-progresso-pulso
  50%
    stroke-opacity: 0.45

@keyframes painel-anel-progresso-respira
  50%
    transform: scale(1.7)
    opacity: 0.5

@keyframes painel-anel-progresso-giro
  to
    transform: rotate(360deg)

@media (prefers-reduced-motion: reduce)
  .painel-anel-progresso__svg--girando,
  .painel-anel-progresso__arco--trabalhando,
  .painel-anel-progresso__ponta
    animation: none

  .painel-anel-progresso__arco
    transition: none
</style>
